/**
 * Timeline Run Entry
 *
 * @module features/releases/components/TimelineRunEntry
 * @description Renders a release run in the timeline as a "band" — a full-width
 * grouping header carrying the single status gauge, the run's own identity,
 * and (when known) the commit it released inline. The commits it covers
 * render as plain rows beneath it (see ReleaseTimeline's grouping). Clicking
 * the title/meta selects the run in the timeline's master-detail pane
 * (`onSelect`) rather than navigating to a separate page — see
 * `ReleasePropertyTimeline`/`ReleaseRunPane`.
 */

import React, { useState } from 'react';
import {
  Text,
  Button,
  Card,
  DialogTrigger,
  Dialog,
  DialogFooter,
  Flex,
  useToast,
} from '@chatsdet/ui';
import { CheckCircle, Warning, XCircle } from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import type {
  TimelineRunEntry as RunEntry,
  TimelineCommitEntry as CommitEntry,
} from '../hooks/useReleaseTimeline';
import type { BandRect } from './ReleaseRunPaneReveal';
import {
  getReleaseRunStatusMeta,
  isActiveStatus,
  nodeStatusFor,
  statusTextClasses,
} from '../releaseTypes';
import { useDeleteReleaseRun } from '../useReleases';
import { SuiteRunLink } from './SuiteRunLink';
import { LiveMarker, TimelineNode } from './TimelineNode';
import { TimelineCommitEntry } from './TimelineCommitEntry';
import { CancelRunDialog } from './CancelRunDialog';
import { ReleaseRowMenu, type ClaimTarget } from './ReleaseRowMenu';
import { BandMetaLine, BandRail, bandCardClassName } from './TimelineBand';
import type { RepoTagInfo } from './ReleaseTimeline';
import type { PromotableCommit } from './PromoteCommitDialog';

type Props = {
  entry: RunEntry;
  /** Whether this band is the run currently shown in the detail pane. */
  isSelected: boolean;
  /**
   * Select this run in the timeline's master-detail pane. `rect` is this
   * band's own vertical slice (top/bottom, measured at click time) so the
   * pane can reveal from that exact position — see `ReleaseRunPaneReveal`.
   */
  onSelect: (runId: string, rect?: BandRect) => void;
  /**
   * The commit this run actually tested up to, paired by SHA against the
   * run's `toSha`/repo checkpoints (see `ReleaseTimeline`'s
   * `releasedCommitByRunId`). Rendered inline in the band instead of as a
   * separate row. `null` when no loaded commit matches the run's checkpoint.
   */
  releasedCommit: CommitEntry | null;
  deployedSha?: string | null;
  repoTag?: RepoTagInfo | null;
  /** See TimelineCommitEntry — reports the released commit up for promotion. */
  onPromoteReleasedCommit?: (commit: PromotableCommit) => void;
  /** The application's sandbox environments, for the menu's Claim items; `null` when not set up for Sandboxes v2. */
  claimTarget: ClaimTarget | null;
};

export function TimelineRunEntry({
  entry,
  isSelected,
  onSelect,
  releasedCommit,
  deployedSha,
  repoTag,
  onPromoteReleasedCommit,
  claimTarget,
}: Props) {
  const { toast } = useToast();
  const deleteMutation = useDeleteReleaseRun();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const bandRef = React.useRef<HTMLDivElement>(null);

  const isDeployed =
    !!releasedCommit &&
    deployedSha !== undefined &&
    deployedSha !== null &&
    releasedCommit.sha === deployedSha;

  const statusMeta = getReleaseRunStatusMeta(entry.status);
  const isActive = isActiveStatus(entry.status);

  const handleClick = () => {
    const rect = bandRef.current?.getBoundingClientRect();
    onSelect(entry.id, rect ? { top: rect.top, bottom: rect.bottom } : undefined);
  };

  const handleConfirmDelete = () => {
    deleteMutation.mutate(entry.id, {
      onSuccess: () => {
        setDeleteModalOpen(false);
        toast.success('Run deleted');
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to delete run');
      },
    });
  };

  const regressionTotal = entry.regressionTestsTotal;
  const regressionPassed = entry.regressionTestsPassed;
  const regressionFailed = entry.regressionTestsFailed;

  const timeStr = entry.startedAt
    ? formatDistanceToNow(new Date(entry.startedAt), { addSuffix: true })
    : formatDistanceToNow(new Date(entry.createdAt), { addSuffix: true });

  // One meta line for the whole band: status, regression outcome, commit
  // count, the tested flag, and (multi-repo) the source repo tag. Deployed
  // is stated once already, via the prominent pill on the embedded commit
  // row above. Each of these used to compete for its own spot across the
  // badge row, the embedded commit's own row, and a separate "N commits"
  // line — collapsed here so the band states its status once.
  const metaParts: React.ReactNode[] = [];
  metaParts.push(
    <Text key="status" size="xs" weight="medium" className={statusTextClasses(entry.status)}>
      {statusMeta.label}
    </Text>
  );
  if (entry.issuesFound > 0) {
    metaParts.push(
      <span key="issues" className="inline-flex items-center gap-0.5 text-red-600 cofactor-dark:text-red-400">
        <Warning size={12} />
        <Text size="xs">
          {entry.issuesFound} issue{entry.issuesFound !== 1 ? 's' : ''}
        </Text>
      </span>
    );
  }
  if (regressionTotal > 0) {
    const regressionLabel =
      regressionFailed > 0
        ? `${regressionFailed} regression${regressionFailed !== 1 ? 's' : ''} failed`
        : `${regressionTotal} regression${regressionTotal !== 1 ? 's' : ''} passed`;
    const regressionColorClass = regressionFailed > 0 ? 'text-red-600 cofactor-dark:text-red-400' : 'text-green-600 cofactor-dark:text-green-400';
    const RegressionIcon = regressionFailed > 0 ? XCircle : CheckCircle;
    const suiteRun = releasedCommit?.suiteRun;
    metaParts.push(
      suiteRun ? (
        <SuiteRunLink
          key="regression"
          suiteRun={suiteRun}
          label={regressionLabel}
          colorClass={regressionColorClass}
          icon={<RegressionIcon size={12} />}
        />
      ) : (
        <span key="regression" className={`inline-flex items-center gap-0.5 ${regressionColorClass}`}>
          <RegressionIcon size={12} />
          <Text size="xs">{regressionLabel}</Text>
        </span>
      )
    );
  }
  if (entry.commitsAnalyzed !== null && entry.commitsAnalyzed > 0) {
    metaParts.push(
      <Text key="commits" size="xs" className="text-muted-foreground">
        {entry.commitsAnalyzed} commit{entry.commitsAnalyzed !== 1 ? 's' : ''}
      </Text>
    );
  }
  if (releasedCommit?.isTested) {
    metaParts.push(
      <Text key="tested" size="xs" className="text-green-600 cofactor-dark:text-green-400">
        Tested
      </Text>
    );
  }
  if (repoTag) {
    metaParts.push(
      <Text key="repo" size="xs" weight="medium" className={`font-mono ${repoTag.textClass}`}>
        {`{${repoTag.label}}`}
      </Text>
    );
  }
  // Last, and on the meta line rather than the right edge of the title row:
  // the right edge is the action slot's, and a timestamp sitting next to the
  // Deployed chip read as part of the control.
  metaParts.push(
    <Text key="time" size="xs" className="text-muted-foreground/70">
      {timeStr}
    </Text>
  );

  return (
    <div className="relative py-1 group/run">
      <BandRail />
      <Card
        ref={bandRef}
        variant="bordered"
        padding="sm"
        className={bandCardClassName(isSelected)}
        aria-current={isSelected ? 'true' : undefined}
      >
        <div className="flex items-start gap-3">
          {/* Single status treatment: the released commit's suite pass-rate
              ring when one exists, otherwise a plain dot in the run's status
              color. Never both a percent AND a status glyph for the same
              event. The node is also this band's ship control — see
              TimelineNode — so clicking it ships `releasedCommit` rather
              than opening the run; that navigation still lives on the
              title/meta line below. */}
          <TimelineNode
            suiteRun={releasedCommit?.suiteRun ?? null}
            status={nodeStatusFor(entry.status)}
            isDeployed={isDeployed}
            ship={
              releasedCommit && onPromoteReleasedCommit
                ? {
                    commit: { sha: releasedCommit.sha, message: releasedCommit.message },
                    onShip: onPromoteReleasedCommit,
                  }
                : undefined
            }
          />

          <div className="flex-1 min-w-0">
            {/* Aligned with the commit sha/message column below it, not the
                node — the node already carries its own "live" halo/ring (see
                TimelineNode), so a marker over the node would be redundant.
                First child of the content column so it sits directly above
                the text it announces. */}
            {isDeployed && (
              <div className="pb-1.5">
                <LiveMarker />
              </div>
            )}
            {/* Identity row — the band's identity is the commit it released,
                not a generic "Release Run" label. Falls back to the run's own
                title only when no released commit could be paired. */}
            {releasedCommit ? (
              <TimelineCommitEntry
                entry={releasedCommit}
                deployedSha={deployedSha}
                repoTag={null}
                embedded
                onMessageClick={handleClick}
              />
            ) : (
              <div className="flex items-baseline gap-2 cursor-pointer group" onClick={handleClick}>
                <Text
                  weight="medium"
                  size="sm"
                  className="min-w-0 flex-1 truncate group-hover:text-primary-600 transition-colors"
                >
                  {entry.title || 'Release run'}
                </Text>
              </div>
            )}

            {/* Single meta line — status, regression outcome, commit count,
                the tested flag, repo tag. See metaParts above. */}
            <BandMetaLine parts={metaParts} onClick={handleClick} />
          </div>

          {/* Actions — one menu per band: Claim in <env> for the released
              commit, then the run's own action (cancel or delete). The band's
              own `TimelineNode` above is the ship control (see TimelineNode's
              module doc); `pt-1` lines this menu up with that node's slot so
              the two read as one right-hand action group. */}
          <ReleaseRowMenu
            claimVersion={
              // Only a resolved commit is claimable: a bare `toSha` can name another repo's commit in a multi-repo config.
              releasedCommit ? { version: releasedCommit.sha, repoFullName: releasedCommit.repoFullName } : null
            }
            claimTarget={claimTarget}
            runAction={
              isActive
                ? { kind: 'cancel', onAction: () => setCancelModalOpen(true) }
                : { kind: 'delete', onAction: () => setDeleteModalOpen(true) }
            }
          />
        </div>
      </Card>

      <CancelRunDialog runId={entry.id} isOpen={cancelModalOpen} onOpenChange={setCancelModalOpen} />

      <DialogTrigger isOpen={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <span style={{ display: 'none' }} />
        <Dialog title="Delete Run">
          <Text>Are you sure you want to delete this run? This action cannot be undone.</Text>
          <DialogFooter>
            <Flex gap="sm">
              <Button variant="outline" onPress={() => setDeleteModalOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onPress={handleConfirmDelete} isDisabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            </Flex>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </div>
  );
}
